/**
 * Preset geometry.
 *
 * ECMA-376 ships ~187 preset shapes, each defined by a guide-formula program.
 * Implementing the full guide evaluator is a module of its own; what is here
 * covers the presets that actually appear in lecture decks, and everything else
 * returns `unsupported` so the caller records honest fidelity debt instead of
 * drawing a wrong shape.
 */
export type PresetResult =
  | { readonly kind: 'rect' }
  | { readonly kind: 'ellipse' }
  | { readonly kind: 'roundRect'; readonly radius: number }
  | { readonly kind: 'path'; readonly path: string }
  | { readonly kind: 'unsupported' };

export interface PresetInput {
  /** CSS pixels. */
  readonly w: number;
  readonly h: number;
  /** `a:avLst` adjust values, in thousandths of a percent as authored. */
  readonly adjustments: Readonly<Record<string, number>>;
}

const RECTS = new Set([
  'rect',
  'flowChartProcess',
  'flowChartPredefinedProcess',
  'actionButtonBlank',
  'textBox',
]);

const ELLIPSES = new Set(['ellipse', 'flowChartConnector', 'flowChartTerminator2']);

export function presetToPath(preset: string, input: PresetInput): PresetResult {
  const { w, h } = input;
  if (w <= 0 || h <= 0) return { kind: 'rect' };
  if (RECTS.has(preset)) return { kind: 'rect' };
  if (ELLIPSES.has(preset)) return { kind: 'ellipse' };

  switch (preset) {
    case 'roundRect':
    case 'flowChartAlternateProcess':
    case 'actionButtonRounded': {
      const adj = adjustment(input, 'adj', 16667);
      return { kind: 'roundRect', radius: round((Math.min(w, h) * adj) / 100000) };
    }

    case 'round1Rect':
    case 'round2SameRect':
    case 'round2DiagRect':
    case 'snip1Rect':
    case 'snip2SameRect':
      // Partially-rounded variants degrade to a plain rectangle: the silhouette
      // is right, only the corner treatment is lost.
      return { kind: 'rect' };

    case 'triangle': {
      const adj = adjustment(input, 'adj', 50000) / 100000;
      return path(`M ${round(w * adj)} 0 L ${round(w)} ${round(h)} L 0 ${round(h)} Z`);
    }

    case 'rtTriangle':
      return path(`M 0 0 L 0 ${round(h)} L ${round(w)} ${round(h)} Z`);

    case 'diamond':
    case 'flowChartDecision':
      return path(
        `M ${round(w / 2)} 0 L ${round(w)} ${round(h / 2)} L ${round(w / 2)} ${round(h)} L 0 ${round(h / 2)} Z`,
      );

    case 'parallelogram':
    case 'flowChartInputOutput': {
      const adj = adjustment(input, 'adj', 25000) / 100000;
      const off = round(Math.min(w, h) * adj);
      return path(`M ${off} 0 L ${round(w)} 0 L ${round(w - off)} ${round(h)} L 0 ${round(h)} Z`);
    }

    case 'trapezoid': {
      const adj = adjustment(input, 'adj', 25000) / 100000;
      const off = round(Math.min(w, h) * adj);
      return path(`M 0 ${round(h)} L ${off} 0 L ${round(w - off)} 0 L ${round(w)} ${round(h)} Z`);
    }

    case 'pentagon':
      return path(polygon(w, h, 5, -90));

    case 'hexagon':
      return path(polygon(w, h, 6, 0));

    case 'heptagon':
      return path(polygon(w, h, 7, -90));

    case 'octagon':
      return path(polygon(w, h, 8, 22.5));

    case 'star4':
      return path(star(w, h, 4, 0.4));
    case 'star5':
      return path(star(w, h, 5, 0.38));
    case 'star6':
      return path(star(w, h, 6, 0.42));
    case 'star8':
      return path(star(w, h, 8, 0.45));

    case 'rightArrow': {
      const body = adjustment(input, 'adj1', 50000) / 100000;
      const head = adjustment(input, 'adj2', 50000) / 100000;
      const hx = round(w * (1 - head));
      const y1 = round((h * (1 - body)) / 2);
      const y2 = round(h - y1);
      return path(
        `M 0 ${y1} L ${hx} ${y1} L ${hx} 0 L ${round(w)} ${round(h / 2)} L ${hx} ${round(h)} L ${hx} ${y2} L 0 ${y2} Z`,
      );
    }

    case 'leftArrow': {
      const body = adjustment(input, 'adj1', 50000) / 100000;
      const head = adjustment(input, 'adj2', 50000) / 100000;
      const hx = round(w * head);
      const y1 = round((h * (1 - body)) / 2);
      const y2 = round(h - y1);
      return path(
        `M ${round(w)} ${y1} L ${hx} ${y1} L ${hx} 0 L 0 ${round(h / 2)} L ${hx} ${round(h)} L ${hx} ${y2} L ${round(w)} ${y2} Z`,
      );
    }

    case 'downArrow': {
      const body = adjustment(input, 'adj1', 50000) / 100000;
      const head = adjustment(input, 'adj2', 50000) / 100000;
      const hy = round(h * (1 - head));
      const x1 = round((w * (1 - body)) / 2);
      const x2 = round(w - x1);
      return path(
        `M ${x1} 0 L ${x2} 0 L ${x2} ${hy} L ${round(w)} ${hy} L ${round(w / 2)} ${round(h)} L 0 ${hy} L ${x1} ${hy} Z`,
      );
    }

    case 'upArrow': {
      const body = adjustment(input, 'adj1', 50000) / 100000;
      const head = adjustment(input, 'adj2', 50000) / 100000;
      const hy = round(h * head);
      const x1 = round((w * (1 - body)) / 2);
      const x2 = round(w - x1);
      return path(
        `M ${round(w / 2)} 0 L ${round(w)} ${hy} L ${x2} ${hy} L ${x2} ${round(h)} L ${x1} ${round(h)} L ${x1} ${hy} L 0 ${hy} Z`,
      );
    }

    case 'chevron':
    case 'homePlate': {
      const adj = adjustment(input, 'adj', 50000) / 100000;
      const notch = round(Math.min(w, h) * adj);
      const back = preset === 'chevron' ? notch : 0;
      return path(
        `M 0 0 L ${round(w - notch)} 0 L ${round(w)} ${round(h / 2)} L ${round(w - notch)} ${round(h)} L 0 ${round(h)} L ${back} ${round(h / 2)} Z`,
      );
    }

    case 'plus':
    case 'mathPlus': {
      const adj = adjustment(input, 'adj', 25000) / 100000;
      const ax = round(w * adj);
      const ay = round(h * adj);
      return path(
        `M ${ax} 0 L ${round(w - ax)} 0 L ${round(w - ax)} ${ay} L ${round(w)} ${ay} L ${round(w)} ${round(h - ay)} L ${round(w - ax)} ${round(h - ay)} L ${round(w - ax)} ${round(h)} L ${ax} ${round(h)} L ${ax} ${round(h - ay)} L 0 ${round(h - ay)} L 0 ${ay} L ${ax} ${ay} Z`,
      );
    }

    case 'line':
    case 'straightConnector1':
      return path(`M 0 0 L ${round(w)} ${round(h)}`);

    case 'bentConnector2':
      return path(`M 0 0 L ${round(w)} 0 L ${round(w)} ${round(h)}`);

    case 'bentConnector3':
      return path(
        `M 0 0 L ${round(w / 2)} 0 L ${round(w / 2)} ${round(h)} L ${round(w)} ${round(h)}`,
      );

    case 'flowChartDocument':
      return path(
        `M 0 0 L ${round(w)} 0 L ${round(w)} ${round(h * 0.84)} Q ${round(w * 0.75)} ${round(h)} ${round(w / 2)} ${round(h * 0.9)} Q ${round(w * 0.25)} ${round(h * 0.8)} 0 ${round(h * 0.95)} Z`,
      );

    case 'can':
    case 'cube':
    case 'cloud':
    case 'heart':
    case 'smileyFace':
    case 'donut':
    case 'blockArc':
    case 'pie':
    case 'arc':
    case 'chord':
      return { kind: 'unsupported' };

    default:
      // Every callout family — wedge*, callout1..3, borderCallout1..3,
      // accentCallout1..3, cloudCallout — loses its tail but keeps the body,
      // which is the part with the text in it.
      if (preset.toLowerCase().includes('callout')) {
        return { kind: 'roundRect', radius: round(Math.min(w, h) * 0.08) };
      }
      return { kind: 'unsupported' };
  }
}

function adjustment(input: PresetInput, name: string, fallback: number): number {
  const v = input.adjustments[name];
  return typeof v === 'number' && Number.isFinite(v) ? v : fallback;
}

function polygon(w: number, h: number, sides: number, startAngleDeg: number): string {
  const cx = w / 2;
  const cy = h / 2;
  const points: string[] = [];
  for (let i = 0; i < sides; i += 1) {
    const angle = ((startAngleDeg + (360 / sides) * i) * Math.PI) / 180;
    points.push(`${round(cx + cx * Math.cos(angle))} ${round(cy + cy * Math.sin(angle))}`);
  }
  return `M ${points.join(' L ')} Z`;
}

function star(w: number, h: number, points: number, innerRatio: number): string {
  const cx = w / 2;
  const cy = h / 2;
  const coords: string[] = [];
  for (let i = 0; i < points * 2; i += 1) {
    const ratio = i % 2 === 0 ? 1 : innerRatio;
    const angle = ((-90 + (360 / (points * 2)) * i) * Math.PI) / 180;
    coords.push(`${round(cx + cx * ratio * Math.cos(angle))} ${round(cy + cy * ratio * Math.sin(angle))}`);
  }
  return `M ${coords.join(' L ')} Z`;
}

function path(d: string): PresetResult {
  return { kind: 'path', path: d };
}

function round(n: number): number {
  return Math.round(n * 100) / 100;
}
